import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { requestGetSites } from '../api/site'
import { DEFAULT_MY_SITE_PAGINATION, DEFAULT_SITE_FILTERS } from '../constants/mySiteProvider'
import { AuthContext } from './auth'
import { ProfileContext } from './profile'

export const MySiteContext = React.createContext({})

export const MySiteProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext)
  const { profile } = useContext(ProfileContext)
  const countsFetchGenRef = useRef(0)

  const [isMySite, setIsMySite] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [sites, setSites] = useState([])
  const [pagination, setPagination] = useState(() => ({ ...DEFAULT_MY_SITE_PAGINATION }))
  const [allSiteLength, setAllSiteLength] = useState(0)
  const [mySiteLength, setMySiteLength] = useState(0)
  const [sitesListRefreshNonce, setSitesListRefreshNonce] = useState(0)
  const [siteFilters, setSiteFilters] = useState(() => ({ ...DEFAULT_SITE_FILTERS }))

  const applyCountResult = useCallback((mySite, apiTotal, fetchGen) => {
    if (fetchGen !== countsFetchGenRef.current) {
      return
    }
    if (mySite) {
      setMySiteLength(apiTotal)
      return
    }
    setAllSiteLength(apiTotal)
  }, [])

  const resetSiteCountsState = useCallback(() => {
    setAllSiteLength(0)
    setMySiteLength(0)
    setSites([])
    setIsMySite(false)
    setPagination({ ...DEFAULT_MY_SITE_PAGINATION })
    setSiteFilters({ ...DEFAULT_SITE_FILTERS })
    setSitesListRefreshNonce(0)
  }, [])

  const getSitesCount = useCallback((mySite) => {
    const fetchGen = countsFetchGenRef.current
    setIsLoading(true)
    const params = {
      resultsPerPage: 12,
      currentPage: 1,
      toggle: mySite
    }

    return requestGetSites(params)
      .then((data) => {
        const apiTotal = data?.pagination?.totalNumber || 0
        applyCountResult(mySite, apiTotal, fetchGen)
      })
      .finally(() => {
        if (fetchGen === countsFetchGenRef.current) {
          setIsLoading(false)
        }
      })
  }, [applyCountResult])

  const refreshSiteCounts = useCallback(() => {
    const fetchGen = ++countsFetchGenRef.current
    setIsLoading(true)

    const params = {
      resultsPerPage: 12,
      currentPage: 1
    }

    return Promise.all([
      requestGetSites({ ...params, toggle: true }),
      requestGetSites({ ...params, toggle: false })
    ])
      .then(([mySitesData, allSitesData]) => {
        applyCountResult(true, mySitesData?.pagination?.totalNumber || 0, fetchGen)
        applyCountResult(false, allSitesData?.pagination?.totalNumber || 0, fetchGen)
      })
      .finally(() => {
        if (fetchGen === countsFetchGenRef.current) {
          setIsLoading(false)
        }
      })
  }, [applyCountResult])

  const refreshSites = useCallback(() => {
    setSitesListRefreshNonce((n) => n + 1)
  }, [])

  const resetFilters = useCallback(() => {
    setPagination({ ...DEFAULT_MY_SITE_PAGINATION })
    setSites([])
    setSiteFilters({ ...DEFAULT_SITE_FILTERS })
  }, [])

  const updateSitesFilters = useCallback((filterId, value) => {
    setSiteFilters((prev) => {
      switch (filterId) {
        case 'year':
          return { ...prev, year: value }
        case 'country':
          return { ...prev, country: value }
        case 'status':
          return { ...prev, status: value }
        default:
          return prev
      }
    })
  }, [])

  const updateFilteredSites = useCallback((data) => {
    setSites(data)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !profile?.id) {
      countsFetchGenRef.current += 1
      resetSiteCountsState()
      setIsLoading(false)
      return
    }

    refreshSiteCounts()
  }, [isAuthenticated, profile?.id, refreshSiteCounts, resetSiteCountsState])

  const contextValue = useMemo(
    () => ({
      isMySite,
      setIsMySite,
      allSiteLength,
      mySiteLength,
      setMySiteLength,
      setAllSiteLength,
      getSitesCount,
      updateSitesFilters,
      siteFilters,
      updateFilteredSites,
      sites,
      resetFilters,
      pagination,
      setPagination,
      isLoading,
      setIsLoading,
      refreshSiteCounts,
      refreshSites,
      sitesListRefreshNonce
    }),
    [
      isMySite,
      allSiteLength,
      mySiteLength,
      getSitesCount,
      updateSitesFilters,
      siteFilters,
      updateFilteredSites,
      sites,
      resetFilters,
      pagination,
      isLoading,
      refreshSiteCounts,
      refreshSites,
      sitesListRefreshNonce
    ]
  )

  return <MySiteContext.Provider value={contextValue}>{children}</MySiteContext.Provider>
}

MySiteProvider.propTypes = {
  children: PropTypes.node.isRequired
}
